import { cn } from '@shared/lib/cn'
import { Button } from '@shared/ui/button'
import { HeroLayout } from '@shared/ui/hero-layout'
import { Input } from '@shared/ui/input'
import { useRef } from 'react'
import { HiMiniCheck } from 'react-icons/hi2'

interface Step2BirthdayProps {
  readonly name: string
  readonly birthYear: string
  readonly birthMonth: string
  readonly birthDay: string
  readonly isLunar: boolean
  readonly onBirthYearChange: (v: string) => void
  readonly onBirthMonthChange: (v: string) => void
  readonly onBirthDayChange: (v: string) => void
  readonly onIsLunarChange: (v: boolean) => void
  readonly onBack: () => void
  readonly onNext: () => void
}

export function Step2Birthday({
  name,
  birthYear,
  birthMonth,
  birthDay,
  isLunar,
  onBirthYearChange,
  onBirthMonthChange,
  onBirthDayChange,
  onIsLunarChange,
  onBack,
  onNext,
}: Step2BirthdayProps) {
  const monthRef = useRef<HTMLInputElement>(null)
  const dayRef = useRef<HTMLInputElement>(null)

  return (
    <HeroLayout
      characterImage="/character-assets/2_form_cleaned.png"
      progressStep={2}
      title="생년월일을 알려주세요"
      subtitle="정확한 사주 분석의 기본이 돼요"
      onBack={onBack}
      footer={
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!(birthYear.length === 4 && birthMonth.length >= 1 && birthDay.length >= 1)}
          onClick={onNext}
        >
          다음
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Date Inputs */}
        <div className="flex flex-col gap-2">
          <span className="typo-caption font-medium text-muted-foreground">생년월일</span>
          <div className="flex gap-2">
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="YYYY"
              maxLength={4}
              className="h-12 flex-[2] rounded-xl text-center font-mono typo-body"
              value={birthYear}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                onBirthYearChange(val)
                if (val.length === 4) {
                  monthRef.current?.focus()
                }
              }}
            />
            <Input
              ref={monthRef}
              type="tel"
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
              className="h-12 flex-1 rounded-xl text-center font-mono typo-body"
              value={birthMonth}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                onBirthMonthChange(val)
                if (val.length === 2) {
                  dayRef.current?.focus()
                }
              }}
            />
            <Input
              ref={dayRef}
              type="tel"
              inputMode="numeric"
              placeholder="DD"
              maxLength={2}
              className="h-12 flex-1 rounded-xl text-center font-mono typo-body"
              value={birthDay}
              onChange={(e) => {
                onBirthDayChange(e.target.value.replace(/\D/g, '').slice(0, 2))
              }}
            />
          </div>
        </div>

        {/* Solar/Lunar toggle */}
        <div className="flex flex-col gap-2">
          <span className="typo-caption font-medium text-muted-foreground">양력/음력</span>
          <div className="flex gap-2">
            {[
              { label: '양력', value: false },
              { label: '음력', value: true },
            ].map((opt) => {
              const isSelected = isLunar === opt.value
              return (
                <button
                  key={opt.label}
                  type="button"
                  className={cn(
                    'flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl typo-label font-medium transition-all',
                    isSelected
                      ? 'border border-primary bg-primary/10 text-foreground'
                      : 'border border-border bg-bg-surface text-muted-foreground',
                  )}
                  onClick={() => {
                    onIsLunarChange(opt.value)
                  }}
                >
                  {isSelected && <HiMiniCheck className="text-primary" />}
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </HeroLayout>
  )
}
